import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { getWinesFromArticle, getArticleFromSlug, getArticle } from "../../../lib/articleRequests";
import { Article } from "../../../context/articlesContext/reducer";
import WineFilters, { IFilter } from "../Wines/WineFilters";
import ArticleFilterDropDown from "../Wines/ArticleFilterDropDown"
import DropDown from "../Inputs/DropDown";
import Button from "../../global/Buttons/Button";
import Loading from "../Loading";
import WineVintage from "../Wines/WineVintage";
import Pagination from "../Pagination/Pagination";
import { getParam } from "../../../utils/helper";
import { useValidSubscription } from "../../../hooks/useValidSubscription";
import { lockScoreSortOptions } from "../../../utils/wine";
import { NumberParam, useQueryParam, withDefault } from "use-query-params";

const ArticleWines = ({ article: initialArticle = null }: { article?: Article }) => {
  const router = useRouter();
  const { slug } = router.query;

  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 1));
  const [perPage, setPerPage] = useQueryParam('per_page', withDefault(NumberParam, 25));

  const [loading, setLoading] = useState(false);
  const [article, setArticle] = useState(initialArticle || ({} as Article));
  const [wines, setWines] = useState([] as any);
  const [sortDirection, setSortDirection] = useState(router.query.order === "asc");
  const [openAll, setOpenAll] = useState(true);
  const hasValidSubscription = useValidSubscription();
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState(
    initialArticle?.id
      ? ({ articleId: initialArticle.id } as IFilter)
      : ({} as IFilter)
  );

  const areFiltersEqual = (left: IFilter = {}, right: IFilter = {}) => {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;
    return leftKeys.every((key) => left[key] === right[key]);
  };


  const sortOptions = [
    {
      label: "Sort by Vintage",
      value: "vintage",
      sort_term: "vintage"
    },
    {
      label: "Sort by Name",
      value: "wine_name",
      sort_term: "wine_name.raw"
    },
    {
      label: "Sort by Score",
      value: "score",
      sort_term: "score"
    },
    {
      label: "Sort by Producer",
      value: "producer_name",
      sort_term: "producer.producer_name.raw"
    },
    {
      label: "Sort by Drinking Window",
      value: "drinking_window_begin",
      sort_term: "drinking_window_begin"
    },
  ]

  // Seed from the URL: the article arrives via getServerSideProps, so the first
  // search runs on mount - before a setSort from updateFiltersFromParams lands.
  const [sort, setSort] = useState(
    () => sortOptions.find((option) => option.value === router.query.orderType) || sortOptions[0]
  );

  const handlePageChange = ({ page: newPage, perPage: newPerPage }) => {
    setPage(newPage);
    setPerPage(newPerPage);
  };

  const handleFilterChange = (e) => {
    setFilterOpen(false);
    const nextFilters = {...e, articleId: filters.articleId};
    if (!areFiltersEqual(nextFilters, filters)) {
      setFilters(nextFilters);
    }
  };

  const formatWines = (winesRaw: any[]) => {
    return winesRaw.map((raw) => {
      return {
        ...raw._source
      }
    });
  };

  const formatQueryParams = () => {
    return {
      "article_id": article.id,
      "page": page,
      "per_page": perPage,
      "api_format": "paginated",
      "filters[color]": filters.color,
      "filters[country]": filters.country,
      "filters[price_range_min]": filters.priceBetweenMin,
      "filters[price_range_max]": filters.priceBetweenMax,
      "filters[review_date_range_min]": filters.reviewDateMin,
      "filters[review_date_range_max]": filters.reviewDateMax,
      "filters[created_at_from]": filters.createdAtFrom,
      "filters[created_at_to]": filters.createdAtTo,
      "filters[score_range_min]": filters.scoreBetweenMin,
      "filters[score_range_max]": filters.scoreBetweenMax,
      "filters[vintage_range_min]": filters.vintageYearMin,
      "filters[vintage_range_max]": filters.vintageYearMax,
      "order": sort.value || "vintage",
      "order_type": sortDirection ? "asc" : "desc"
    }
  };

  const createFilterKey = (key: string, isInt: boolean) => {
    const parsedParam = getParam(router.asPath, key);
    if (parsedParam === "") {
      return { [key]: null }
    } else if (isInt) {
      return { [key]: parseInt(parsedParam) }
    } else {
      return { [key]: decodeURIComponent(parsedParam).replace("+", " ") }
    }
  }

  const updateFiltersFromParams = () => {
    const queryFilters = {
      ...createFilterKey("vintageYearMin", true),
      ...createFilterKey("vintageYearMax", true),
      ...createFilterKey("scoreBetweenMin", true),
      ...createFilterKey("scoreBetweenMax", true),
      ...createFilterKey("priceBetweenMin", true),
      ...createFilterKey("priceBetweenMax", true),
      ...createFilterKey("reviewDateMin", true),
      ...createFilterKey("reviewDateMax", true),
      ...createFilterKey("createdAtFrom", false),
      ...createFilterKey("createdAtTo", false),
      ...createFilterKey("country", false),
      ...createFilterKey("color", false),
      ...filters,
    };

    Object.keys(queryFilters).forEach(key => queryFilters[key] === null && delete queryFilters[key]);
    if (!areFiltersEqual(queryFilters, filters)) {
      setFilters({
        ...queryFilters
      });
    }

    const sortBy = getParam(router.asPath, "orderType");
    if (sortBy !== "") {
      const sortObject = sortOptions.find((option) => option.value === sortBy);
      if (sortObject) {
        setSort(sortObject);
      }
    }

    const sortOrder = getParam(router.asPath, "order");
    if (sortOrder !== "") {
      setSortDirection(sortOrder === "asc" ? true : false); 
    }

    return Object.keys(queryFilters).length > 1; //articleId should always be set
  };

  
  const handleSearch = () => {
    
    setWines([]);
    if(!loading) setLoading(true);
    getWinesFromArticle(article.id, formatQueryParams()).then((response) => {
      setWines({
        wines: formatWines(response.data.wines),
        wines_total: response.data.wines_total
      });
      
      updateRouterToLatest();
      
      setLoading(false);
    });
  };

  const updateRouterToLatest = () => {
    // replace, not push: this runs after every search - including the one on
    // mount - so pushing left a history entry per filter/sort/page change and
    // trapped the back button on the page. shallow keeps getServerSideProps
    // from re-running, so the article is not re-fetched on each search.
    router.replace(
      {
        pathname: `/articles/${article.slug}/reviews`,
        query: { 
          ...filters,
          page,
          per_page: perPage,
          order: sortDirection ? "asc" : "desc",
          orderType: sort.value
        }
      }, 
      undefined, { shallow: true }
    )
  };

  const handleArticleChange = (articleId: number) => {
    setLoading(true);
    getArticle(articleId.toString()).then((response) => {
      setArticle(response.data);
    });
  };

  useEffect(() => {
    if(slug && slug !== article.slug && !loading) {
      setLoading(true);
      updateFiltersFromParams();
      getArticleFromSlug(slug as string).then((response) => {
        setFilters((prevFilters) => ({ ...prevFilters, articleId: response.data.article.id }));
        setArticle(response.data.article);
        setLoading(false);
      });
    }
  }, [slug]);

  useEffect(() => {
    if (article && article.id) {
      if (!updateFiltersFromParams()) {
        handleSearch();
      }
    }
  }, [article]);

  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (!loading && article && article.id) {
      handleSearch();
    }
  }, [filters, sort, sortDirection, page, perPage]);  
  
  return (
    <div>
      <div className="pb-8 w-full mx-auto">
        <h1 className="text-3xl font-bold pt-4 text-center">
          { article.title }
        </h1>
      </div>
      {article && article.id && <ArticleFilterDropDown
        className={'pb-2 md:pb-4'}
        onArticleChange={(article) => handleArticleChange(article.value)}
        articleId={article.id.toString()}
        clearable={false}
      />}
      <div className="lg:flex lg:items-start lg:space-x-8 relative">
        <WineFilters 
          className={`lg:block ${ filterOpen ? "block" : "hidden" } fixed`}
          values={filters}
          onApply={(e) => {
            handleFilterChange(e)
          }}
        />
        <Button
          type="primary"
          label="Show Advanced Options"
          labelColor="text-black"
          className="mb-4 lg:hidden bg-opacity-20"
          onClick={() => {
            setFilterOpen(true);
          }}
        />    
        { loading && 
          <div className="flex w-full">
            <Loading isLoading={loading} />
          </div>
        }
        {
          !loading && wines && wines.wines &&
          <div className="w-full">
            <div className="space-y-4">
              <div className="flex items-center flex-col sm:flex-row bg-white">
                <h2 className="text-xl w-full pb-3 lg:pb-0">{`Reviews (${wines.wines_total})`}</h2>
                <div className="flex items-center justify-end justify-self-end ml-4 mr-4 pb-4 w-full xl:w-1/2">
                  <DropDown 
                    type="line"
                    placeholder="Sort By"
                    className="w-full"
                    options={lockScoreSortOptions(sortOptions, !hasValidSubscription)}
                    value={sort.label}
                    onOptionSelect={(e) => {
                      setSort(e);
                    }}
                  />
                  <div
                    className={`transition cursor-pointer ml-5 hover:text-red hover:opacity-100 ${ !sort ? "opacity-20" : "opacity-100" }`}
                    onClick={() => {
                      setSortDirection(!sortDirection);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setSortDirection(!sortDirection)}}
                    aria-label={sortDirection ? "Sort in ascending order" : "Sort in descending order"}
                  >
                    {
                      sortDirection ?
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                      </svg>
                      :
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                      </svg>
                    }
                  </div>
                  <div 
                    className="transition w-4 pl-5 pr-3 cursor-pointer hover:text-red"
                    onClick={() => {
                      setOpenAll(!openAll);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter") setOpenAll(!openAll)} }
                    aria-label={openAll ? "Collapse all" : "Expand all"}
                  >
                    {
                      openAll ?
                      <svg width="16px" height="16px" viewBox="0 0 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                          <g id="Page-1" strokeWidth="1" fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                              <g id="v" transform="translate(-366.000000, -283.000000)" strokeWidth="2">
                                  <g id="Group-Copy" transform="translate(367.000000, 284.000000)">
                                      <path d="M12.5,1.5 L12.5,5.5 M12.5,5.5 L16.5,5.5 M12.5,5.5 L17.5,0.5 M5.5,16.5 L5.5,12.5 M5.5,12.5 L1.5,12.5 M5.5,12.5 L0.5,17.5" id="Shape"></path>
                                  </g>
                              </g>
                          </g>
                      </svg>
                      :
                      <svg width="16px" height="16px" viewBox="0 0 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                          <g id="Page-1" strokeWidth="1" fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                              <g id="v" transform="translate(-327.000000, -284.000000)" strokeWidth="2">
                                  <g id="Group" transform="translate(327.000000, 284.000000)">
                                      <path d="M17,5 L17,1 M17,1 L13,1 M17,1 L12,6 M1,13 L1,17 M1,17 L5,17 M1,17 L6,12" id="Shape"></path>
                                  </g>
                              </g>
                          </g>
                      </svg>
                    }
                  </div>
                </div>
              </div>
              {
                wines && wines.wines.length > 0 &&
                wines.wines.map((wine) => {
                  return (
                    <WineVintage
                      key={wine.id}
                      wine={wine}
                      producerName={wine.producer.producer_name}
                      slug={wine.slug}
                      open={openAll}
                      useBaseWineId={true}
                    />
                  )
                })
              }
            </div>
          </div>
        }
      </div>
      {(
        wines && wines.wines_total > perPage &&
        <Pagination
          records={wines.wines_total}
          onPageChange={(e) => {
            handlePageChange(e);
          }}
        />
      )}
    </div>
  )
}

export default ArticleWines;
